import { useId, type SelectHTMLAttributes } from 'react';
import type { ZodError } from 'zod';

import { Field, Select as UISelect } from '@/components/ui';

import { useFieldContext } from './contexts';

export type Option<TValue extends string = string> = { value: TValue; label: string; disabled?: boolean };

type SelectProps<TValue extends string> = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'value' | 'onChange' | 'onBlur' | 'children'
> & {
  label?: string;
  helperText?: string;
  required?: boolean;
  placeholder?: string;
  options: Option<TValue>[];
};

const firstError = (errors: ZodError[]) => errors.find((e) => e !== undefined)?.message;

export function Select<TValue extends string>({
  label,
  helperText,
  required,
  placeholder,
  options,
  id,
  ...props
}: SelectProps<TValue>) {
  const reactId = useId();
  const selectId = id ?? reactId;
  const field = useFieldContext<TValue>();
  const touched = field.state.meta.isTouched;
  const errMsg = touched ? firstError(field.state.meta.errors) : undefined;

  return (
    <Field label={label} required={required} error={errMsg} hint={!errMsg ? helperText : undefined} htmlFor={selectId}>
      <UISelect
        id={selectId}
        value={field.state.value ?? ''}
        onChange={(e) => field.handleChange(e.target.value as TValue)}
        onBlur={field.handleBlur}
        error={!!errMsg}
        required={required}
        placeholder={placeholder}
        options={options}
        {...props}
      />
    </Field>
  );
}
